// Aftergrow site behavior.
// 1. In-page jumps (spec 2.6): links to a section on this page jump there instantly, keep the
//    query string (for example ad tracking), leave room for the sticky header (scroll-margin-top
//    in aftergrow.css) and move focus to the section heading. A jump to #offer also selects the
//    90-Day Protocol (conductor note 1). A jump from the menu or cart drawer closes the drawer
//    first, so its focus trap and scroll lock let go (Slice 3). Without JS the browser's own jump
//    still works.
// 2. Email forms (spec 4): check the address before sending and show the error text; after a
//    sign-up only the form that was sent says thanks.
// 3. Contact form (pages spec 4.7): check the required fields, mark the wrong ones in red and
//    show the error text. Shopify still checks the email on the server.
(() => {
  const selectProtocol = (section) => {
    const radio = section.querySelector('input[data-ag-plan="protocol"]');
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  // A FAQ row (<details>) opens; the plan list focuses the chosen plan (Slice 4a). On the product
  // page #offer keeps the chosen plan ("Compare plans", prompt 04a.2).
  const arrive = (section, id) => {
    if (id === 'offer' && !section.hasAttribute('data-url-plan')) selectProtocol(section);
    if (section.matches('details')) section.open = true;
    const target =
      section.querySelector('[data-ag-focus-target]') ||
      section.querySelector('summary, input[data-ag-plan]:checked');
    if (target) target.focus({ preventScroll: true });
  };

  const jump = (section, id) => {
    section.scrollIntoView({ block: 'start' });
    arrive(section, id);
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href*="#"]');
    if (!link) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.hash) return;
    const id = decodeURIComponent(url.hash.slice(1));
    const section = id ? document.getElementById(id) : null;
    if (!section) return;
    event.preventDefault();
    if (window.location.hash !== url.hash) window.history.pushState(null, '', url.hash);
    // The menu drawer or the cart drawer ("Shop the serum") closes first, then the page jumps.
    const drawer = link.closest('header-drawer, theme-drawer');
    if (drawer && typeof drawer.close === 'function') {
      Promise.resolve(drawer.close()).then(() => requestAnimationFrame(() => jump(section, id)));
    } else {
      jump(section, id);
    }
  });

  // Arriving from another page on /#offer or /#month-three.
  const startId = decodeURIComponent(window.location.hash.slice(1));
  const start = startId ? document.getElementById(startId) : null;
  // After load: Horizon's components blur the page while they start up (Slice 4a).
  if (start && start.matches('.aftergrow-anchor, [data-ag-offer], details, fieldset')) {
    const later = () => setTimeout(() => arrive(start, startId), 60);
    if (document.readyState === 'complete') later();
    else window.addEventListener('load', later, { once: true });
  }

  // Image cards on phones: the "1 / 4" counter follows the swipe.
  document.querySelectorAll('[data-ag-cards]').forEach((cards) => {
    const track = cards.querySelector('.ag-cards__track');
    const now = cards.querySelector('[data-ag-cards-now]');
    if (!track || !now) return;
    track.addEventListener('scroll', () => {
      const card = track.firstElementChild;
      if (card) now.textContent = Math.round(track.scrollLeft / (card.offsetWidth || 1)) + 1;
    }, { passive: true });
  });

  // Product photo thumbnails (phones): show that photo in Horizon's gallery and scroll back to it.
  document.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-ag-thumb]');
    const gallery = thumb && document.querySelector('media-gallery slideshow-component, slideshow-component');
    if (!gallery) return;
    gallery.select?.(Number(thumb.dataset.agThumb));
    gallery.scrollIntoView({ block: 'start' });
  });

  // Marks a field as wrong (red border, aria-invalid, linked to the error text) or clears it.
  const mark = (input, error, bad) => {
    if (bad) {
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', error.id);
    } else {
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }
  };
  const valid = (input) => input.value.trim() !== '' && input.checkValidity();

  document.querySelectorAll('.ag-email__form').forEach((form) => {
    const input = form.querySelector('input[type="email"]');
    const error = form.querySelector('[data-ag-email-error]');
    if (!input || !error) return;
    form.noValidate = true;
    form.addEventListener('submit', (event) => {
      if (!valid(input)) {
        event.preventDefault();
        error.hidden = false;
        mark(input, error, true);
        input.focus();
      }
    });
    input.addEventListener('input', () => {
      if (!error.hidden && valid(input)) {
        error.hidden = true;
        mark(input, error, false);
      }
    });
  });

  // After a guide sign-up Shopify reloads with ?customer_posted=true and every guide form on the
  // page would say thanks: keep it on the form that was sent (else the page's own form).
  document.addEventListener('submit', (event) => {
    try {
      if (event.target.matches('.ag-email__form')) sessionStorage.setItem('agSent', event.target.id);
    } catch (e) {}
  });
  if (/[?&]customer_posted=true/.test(window.location.search)) {
    let sent = null;
    try {
      sent = sessionStorage.getItem('agSent');
    } catch (e) {}
    const done = [...document.querySelectorAll('.ag-email__form')].filter((f) => f.querySelector('.ag-email__success'));
    const keep = done.find((f) => f.id === sent) || done.find((f) => !f.closest('footer')) || done[0];
    done.forEach((f) => f === keep || f.querySelector('.ag-email__success').remove());
  }

  document.querySelectorAll('.ag-contact__form').forEach((form) => {
    const error = form.querySelector('[data-ag-contact-error]');
    const fields = [...form.querySelectorAll('[required]')];
    if (!error || !fields.length) return;
    form.noValidate = true;
    form.addEventListener('submit', (event) => {
      const wrong = fields.filter((field) => !valid(field));
      fields.forEach((field) => mark(field, error, wrong.includes(field)));
      error.hidden = wrong.length === 0;
      if (wrong.length) {
        event.preventDefault();
        wrong[0].focus();
      }
    });
    fields.forEach((field) => {
      field.addEventListener('input', () => {
        if (field.getAttribute('aria-invalid') === 'true' && valid(field)) mark(field, error, false);
        if (!fields.some((f) => f.getAttribute('aria-invalid') === 'true')) error.hidden = true;
      });
    });
  });

  // Tabs (prompt 04a.2): a click or the arrow, Home and End keys choose a tab.
  const choose = (tab) => {
    const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[role="tab"]')];
    tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      document.getElementById(t.getAttribute('aria-controls')).toggleAttribute('data-ag-hide', !on);
    });
    tab.focus();
  };
  document.addEventListener('click', (event) => {
    const tab = event.target.closest('.ag-tabs [role="tab"]');
    if (tab) choose(tab);
  });
  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest?.('.ag-tabs [role="tab"]');
    if (!tab) return;
    const tabs = [...tab.parentElement.querySelectorAll('[role="tab"]')];
    const i = tabs.indexOf(tab);
    const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    choose(tabs[(next + tabs.length) % tabs.length]);
  });

  // Announcement bar (prompt 04a.2): Horizon rotates it and pauses on hover; it also pauses while a
  // link in it has focus, and with reduced motion only the first message shows.
  customElements.whenDefined('announcement-bar-component').then(() => {
    document.querySelectorAll('announcement-bar-component[autoplay]').forEach((bar) => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        bar.pause?.();
        bar.current = 0;
        return;
      }
      bar.addEventListener('focusin', () => bar.suspend?.());
      bar.addEventListener('focusout', () => bar.resume?.());
    });
  });
})();
